/**
 * Layout Section Component
 *
 * Renders a section based on page layout configuration from Object Manager.
 * Supports 1 or 2 column layouts, collapsible sections, and field-level formatting.
 */

import React, { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useSectionFields, formatFieldValue } from '../../hooks/useObjectManager';
import { resolveFieldValue } from '../../utils/fieldValueResolver';
import type { LayoutSection as LayoutSectionType } from '../../services/mockData/objectManagerLayouts';
import type { CustomField } from '../../services/queries/fieldQueries';

interface LayoutSectionProps {
  section: LayoutSectionType;
  objectApiName: string;
  record: any;
}

export const LayoutSection: React.FC<LayoutSectionProps> = ({
  section,
  objectApiName,
  record
}) => {
  const [isCollapsed, setIsCollapsed] = useState(
    section.defaultCollapsed || false
  );

  const fields = useSectionFields(objectApiName, section.fields);

  if (fields.length === 0) {

    return (
      <div className="bg-white dark:bg-[#1e293b] rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          {section.title}
        </h3>

        <div className="flex items-center gap-3 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
          <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
            <span className="text-blue-600 dark:text-blue-400 text-xl">ℹ️</span>
          </div>
          <div>
            <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
              This section has no fields configured
            </p>
            <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
              Go to Object Manager → Page Layouts → Visual Builder to add fields to this section.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const handleToggle = () => {
    setIsCollapsed(!isCollapsed);
  };

  return (
    <div className="bg-white dark:bg-[#1e293b] rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
      {/* Section Header */}
      <div
        className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 cursor-pointer hover:bg-gray-50 dark:hover:bg-[#334155]"
        onClick={handleToggle}
      >
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {section.title}
          </h2>
          <button
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            aria-label={isCollapsed ? 'Expand section' : 'Collapse section'}
          >
            {isCollapsed ? (
              <ChevronRight className="w-5 h-5" />
            ) : (
              <ChevronDown className="w-5 h-5" />
            )}
          </button>
        </div>
      </div>

      {/* Section Content */}
      {!isCollapsed && (
        <div className="p-6">
          <div
            className={`grid gap-x-6 gap-y-4 ${
              section.columns === 1 ? 'grid-cols-1' : 'grid-cols-2'
            }`}
          >
            {fields.map(field => {
              if (!field) return null;

              const value = resolveFieldValue(record, field.fieldName);
              const formattedValue = formatFieldValue(value, field.fieldType);

              return (
                <div key={field.fieldName}>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                    {field.fieldLabel}
                  </dt>
                  <dd className="text-sm text-gray-900 dark:text-white">
                    {renderFieldValue(field, value, formattedValue, record)}
                  </dd>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Render field value with appropriate formatting and links
 */
function renderFieldValue(
  field: CustomField,
  value: any,
  formattedValue: string,
  record: any
) {
  // Handle empty values
  if (value === null || value === undefined || value === '') {
    return <span className="text-gray-400 dark:text-gray-500">—</span>;
  }

  // Handle lookup/reference fields
  if (field.fieldType === 'lookup' && value) {
    // Display the lookup field value (name if available, otherwise ID)
    const displayValue = record[`${field.fieldName}Name`] || formattedValue;

    return (
      <span className="text-blue-600 dark:text-blue-400 font-medium">
        {displayValue}
      </span>
    );
  }

  // Handle email fields
  if (field.fieldType === 'email' && value) {
    return (
      <a
        href={`mailto:${value}`}
        className="text-blue-600 dark:text-blue-400 hover:underline"
      >
        {formattedValue}
      </a>
    );
  }

  // Handle phone fields
  if (field.fieldType === 'phone' && value) {
    return (
      <a
        href={`tel:${value}`}
        className="text-blue-600 dark:text-blue-400 hover:underline"
      >
        {formattedValue}
      </a>
    );
  }

  // Handle URL fields
  if (field.fieldType === 'url' && value) {
    return (
      <a
        href={value}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-600 dark:text-blue-400 hover:underline"
      >
        {formattedValue}
      </a>
    );
  }

  // Handle text area (preserve line breaks)
  if (field.fieldType === 'textarea' && value) {
    return <div className="whitespace-pre-wrap">{formattedValue}</div>;
  }

  // Handle picklist fields with badge styling
  if (field.fieldType === 'picklist' && value) {
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
        {formattedValue}
      </span>
    );
  }

  // Handle checkbox/boolean fields
  if (field.fieldType === 'checkbox' && typeof value === 'boolean') {
    return (
      <span className={value ? 'text-green-600 dark:text-green-400' : 'text-gray-400 dark:text-gray-500'}>
        {formattedValue}
      </span>
    );
  }

  // Handle currency fields
  if (field.fieldType === 'currency' && value) {
    return <span className="font-medium">{formattedValue}</span>;
  }

  // Default - return formatted value
  return <span>{formattedValue}</span>;
}
